import { Controller } from "stimulus"

// Generic Donation Flow engine, shared by all clients. 

export default class extends Controller {
  static targets = ["step", "progressNav", "previous", "progress", "form", "wrapper",
    "existingDonorNotice", "existingDonorMessage", "existingDonorLogin"]

  static values = {
    minimumContributionAmount: Number,
    currencyCode: String,
    afterReturnFromSuccessfulStripeCheckout: Boolean,
    // "Take the pledge →" for a pledge checkout (see donation_wizard_data). Plain type, no
    // { type, default } object -- this app's Stimulus version doesn't support value defaults.
    finishLabel: String,
    // Set only for a client whose checkout would replace an existing donor's pledge -- see
    // DonationFlowsController#check_email.
    emailCheckUrl: String
  }

  initialize() {
    this.showPage(this.initialPageId)
  }

  // Swaps variant by loading the other variant's own page and landing back on the step the donor was on, instead of always restarting at the first page.
  get initialPageId() {
    if (this.afterReturnFromSuccessfulStripeCheckoutValue) return this.pageIds[this.pageIds.length - 1]

    const requestedStep = new URLSearchParams(window.location.search).get("step")
    return requestedStep && this.pageIds.includes(requestedStep) ? requestedStep : this.pageIds[0]
  }

  // "allocate" only applies to a portfolio card marked data-requires-allocate (HLI's Recommended
  // Charities, OFTW's "Choose your own mix") -- skip it from Back/Continue/the progress bar entirely
  // for any other selection
  get pageIds() {
    const ids = this.stepTargets.map((el) => el.dataset.pageId)
    if (this.selectedPortfolioIsCustomizable) return ids

    return ids.filter((id) => id !== "allocate")
  }

  get selectedPortfolioIsCustomizable() {
    return Boolean(this.selectedPortfolioCard?.dataset?.requiresAllocate)
  }

  // The picked card -- or before radio-select has marked it (it connects after this controller),
  // the card matching the hidden field's value.
  get selectedPortfolioCard() {
    const step = this.element.querySelector('fieldset[data-page-id="portfolio"]')
    if (!step) return null

    const active = step.querySelector("[data-radio-select-value].is-active")
    if (active) return active

    const selectedId = step.querySelector('[name="donation_form[managed_portfolio_id]"]')?.value
    return selectedId ? step.querySelector(`[data-radio-select-value="${CSS.escape(selectedId)}"]`) : null
  }

  get currentPageIndex() {
    return this.pageIds.indexOf(this.currentPageId)
  }

  async next(event) {
    event.preventDefault()
    this.validateFields()
    if (!this.valid) {
      this.scrollToFirstError()
      return
    }
    if (!(await this.emailMayContinue())) return

    const nextPageId = this.pageIds[this.currentPageIndex + 1]
    if (nextPageId) {
      this.showPage(nextPageId)
    } else {
      this.submitForm()
    }
  }

  // Leaving the step that holds the email: an email that already has an account is sent to log in
  // and manage their pledge there, rather than finding out only once payment details are entered.
  // Fails open -- the PaymentIntent endpoint enforces the same rule server-side.
  async emailMayContinue() {
    if (!this.hasEmailCheckUrlValue || !this.emailCheckUrlValue || !this.hasExistingDonorNoticeTarget) return true

    const step = this.stepTargets.find((el) => el.dataset.pageId === this.currentPageId)
    const emailField = step?.querySelector('[name="donation_form[email]"]')
    if (!emailField) return true
    // A second click while the check is in flight must not advance past the next step too.
    if (this.emailCheckInFlight) return false

    this.emailCheckInFlight = true
    this.existingDonorNoticeTarget.classList.add("is-hidden")
    try {
      const body = new FormData()
      body.set("email", emailField.value)
      body.set("variant", this.element.querySelector('[name="variant"]')?.value || "")
      const response = await fetch(this.emailCheckUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: body
      })
      if (!response.ok) return true

      const data = await response.json()
      if (!data.login_required) return true

      this.existingDonorMessageTarget.textContent = data.message
      this.existingDonorLoginTarget.href = data.login_url
      this.existingDonorNoticeTarget.classList.remove("is-hidden")
      return false
    } catch (err) {
      return true
    } finally {
      this.emailCheckInFlight = false
    }
  }

  // A blocked Continue otherwise looks like a dead button whenever the error is below the fold --
  // e.g. the pledge start's note, under the sticky nav bar on a laptop screen.
  scrollToFirstError() {
    const step = this.stepTargets.find((el) => el.dataset.pageId === this.currentPageId)
    // A flagged required Yes/No is a hidden input -- scroll to the question around it instead.
    const error = [...(step?.querySelectorAll('[role="alert"]:not(.is-hidden), .is-danger, .donation-flow__field-note:not(.is-hidden)') || [])]
      .map((el) => (el.type === "hidden" ? el.closest(".donation-flow__field") : el))
      .find((el) => el && el.offsetParent !== null)
    error?.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  previous(event) {
    event.preventDefault()
    const prevPageId = this.pageIds[this.currentPageIndex - 1]
    if (prevPageId) this.showPage(prevPageId)
  }

  showPage(pageId) {
    const pageChanged = this.currentPageId && this.currentPageId !== pageId
    this.currentPageId = pageId
    this.stepTargets.forEach((el) => {
      el.classList.toggle("is-hidden", el.dataset.pageId !== pageId)
    })
    if (this.hasWrapperTarget) this.wrapperTarget.classList.toggle("donation-flow--wide", pageId === "allocate")
    this.refreshNavButtons()
    this.renderProgress()
    this.renderStepEyebrows()

    const paymentConnection = this.paymentConnectionController
    if (paymentConnection && pageId === this.pageIds[this.pageIds.length - 1]) {
      if (paymentConnection.hasConnectedPaymentMethod()) {
        paymentConnection.ensurePaymentMethodDataIsSet()
        paymentConnection.showConnectedPaymentMethodState()
      } else {
        paymentConnection.showPaymentMethodSelectionInterface()
      }
    }

    if (pageChanged) window.scrollTo({ top: 0, left: 0, behavior: "auto" })
    this.element.dispatchEvent(new CustomEvent("donation-wizard:pageShown", { detail: { pageId }, bubbles: true }))
  }

  get paymentConnectionController() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'payment-connection')
  }

  get donationFlowPaymentController() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'donation-flow-payment')
  }

  refreshNavButtons() {
    const index = this.currentPageIndex
    const isFirst = index === 0
    const isLast = index === this.pageIds.length - 1

    this.progressTarget.innerText = isLast ? (this.finishLabelValue || "Donate now →") : "Continue →"
    this.previousTarget.classList.toggle("is-hidden", isFirst)
  }

  // Mirrors the HLI prototype's JS-rendered progress-nav.
  renderProgress() {
    if (!this.hasProgressNavTarget) return

    const pageIds = this.pageIds
    const index = this.currentPageIndex

    this.progressNavTarget.innerHTML = pageIds.map((pageId, i) => {
      const done = i < index
      const current = i === index
      const stepClass = done ? "prog-step done" : current ? "prog-step current" : "prog-step"
      const label = this.pageLabel(pageId)
      const circle = done
        ? `<button type="button" class="prog-circle-btn" data-page-id="${pageId}" aria-label="Go back to ${label}"><div class="prog-circle">&#10003;</div></button>`
        : `<div class="prog-circle">${i + 1}</div>`

      const line = i < pageIds.length - 1
        ? `<div class="prog-line${done ? " done" : ""}"></div>`
        : ""

      return `<div class="${stepClass}" aria-current="${current}">${circle}<div class="prog-label">${label}</div></div>${line}`
    }).join("")

    this.progressNavTarget.querySelectorAll(".prog-circle-btn").forEach((btn) => {
      btn.addEventListener("click", () => this.showPage(btn.dataset.pageId))
    })
  }

  // A step's "Step 2 of 4" eyebrow, counted over the pages this pick goes through (Allocate is
  // skipped for most) rather than the server-rendered total of every page.
  renderStepEyebrows() {
    const pageIds = this.pageIds
    this.stepTargets.forEach((el) => {
      const eyebrow = el.querySelector?.(".donation-flow__step-eyebrow")
      const index = pageIds.indexOf(el.dataset.pageId)
      if (eyebrow && index >= 0) eyebrow.textContent = `Step ${index + 1} of ${pageIds.length}`
    })
  }

  // Prefers the client-configured data-page-label, falls back to titleizing the page_id.
  pageLabel(pageId) {
    const step = this.stepTargets.find((el) => el.dataset.pageId === pageId)
    if (step?.dataset.pageLabel) return step.dataset.pageLabel

    return pageId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  }

  submitForm() {
    this.validateFields()
    if (!this.valid) return

    const donationFlowPayment = this.donationFlowPaymentController
    if (donationFlowPayment) {
      donationFlowPayment.confirmAndSubmit()
      return
    }

    this.formTarget.submit()
  }

  validateFields() {
    const requiredValid = this.validateRequiredFields()

    // Client controllers add page-specific checks -- format rules, cross-field logic by listening for this event and calling preventDefault() to block advancing.
    const event = new CustomEvent("donation-wizard:validate", {
      detail: { pageId: this.currentPageId }, cancelable: true, bubbles: true
    })
    this.element.dispatchEvent(event)

    this.valid = requiredValid && !event.defaultPrevented
  }

  validateRequiredFields() {
    let anyEmpty = false
    const requiredElements = this.targets.findAll(`required_field_${this.currentPageId}`)

    requiredElements.forEach((element) => {
      // Not asked right now: another currency's donor question (disabled), or one behind a
      // Yes/No gate answered No (hidden) -- see donation_flows/_donor_question.
      if (element.disabled || element.closest(".is-hidden")) return

      let isEmpty
      if (element.type === "checkbox") {
        isEmpty = !element.checked
      } else if (element.tagName === "SELECT") {
        isEmpty = !element.value
      } else {
        isEmpty = element.value === ""
      }
      element.classList.toggle("is-danger", isEmpty)
      anyEmpty = anyEmpty || isEmpty
    })

    return !anyEmpty
  }

  get valid() {
    return this.data.valid
  }

  set valid(value) {
    this.data.valid = value
  }
}
